import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContextValue, AuthState, AuthTokens } from "../types";
import * as authService from "../services/authService";
import {
  isAccessTokenExpired,
  isRefreshTokenExpired,
  KeycloakTokenError,
  refreshTokens,
} from "../services/keycloakTokenService";
import { clearTokens, getTokens, saveTokens } from "../utils/tokenUtils";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const tokensRef = useRef<AuthTokens | null>(null);

  const handleSessionExpired = useCallback(async (errorMessage?: string) => {
    await clearTokens();
    tokensRef.current = null;
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      error: errorMessage || "Sitzung abgelaufen. Bitte melden Sie sich erneut an.",
    });
  }, []);

  const getValidAccessToken = useCallback(async (): Promise<string> => {
    const tokens = tokensRef.current || getTokens();

    if (!tokens || !tokens.refreshToken) {
      throw new Error("Nicht authentifiziert");
    }

    if (isAccessTokenExpired(tokens.accessToken, 30)) {
      try {
        const newTokens = await queryClient.fetchQuery({
          queryKey: ["tokenRefresh"],
          queryFn: async () => {
            // Re-check if tokens were already refreshed by concurrent call
            const currentTokens = tokensRef.current || getTokens();
            if (currentTokens && !isAccessTokenExpired(currentTokens.accessToken, 30)) {
              return currentTokens;
            }

            const config = authService.getKeycloakConfig();
            const refreshedTokens = await refreshTokens(currentTokens!.refreshToken, config);

            await saveTokens(refreshedTokens);
            tokensRef.current = refreshedTokens;
            return refreshedTokens;
          },
          staleTime: 0,
          gcTime: 0,
          retry: 1,
        });

        return newTokens.accessToken;
      } catch (error) {
        console.error("Token refresh failed:", error);

        if (error instanceof KeycloakTokenError && error.isSessionInvalid) {
          await handleSessionExpired(
            "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an."
          );
        } else {
          await handleSessionExpired(
            error instanceof Error
              ? error.message
              : "Sitzung abgelaufen. Bitte melden Sie sich erneut an."
          );
        }
        throw error;
      }
    }

    return tokens.accessToken;
  }, [queryClient, handleSessionExpired]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const tokens = getTokens();

        if (tokens && tokens.refreshToken) {
          if (isRefreshTokenExpired(tokens.refreshToken)) {
            await clearTokens();
            setAuthState({
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            return;
          }

          if (isAccessTokenExpired(tokens.accessToken, 30)) {
            try {
              const config = authService.getKeycloakConfig();
              const newTokens = await refreshTokens(tokens.refreshToken, config);
              await saveTokens(newTokens);
              tokensRef.current = newTokens;
            } catch (error) {
              console.error("Proactive token refresh failed:", error);
              if (error instanceof KeycloakTokenError && error.isSessionInvalid) {
                await clearTokens();
                setAuthState({
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                });
                return;
              }
              tokensRef.current = tokens;
            }
          } else {
            tokensRef.current = tokens;
          }
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : "Authentifizierung konnte nicht initialisiert werden",
        });
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      tokensRef.current = await authService.login();
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Anmeldung fehlgeschlagen";
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });

      throw error;
    }
  }, []);


  const logout = useCallback(async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

      tokensRef.current = null;
      await authService.logout();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      tokensRef.current = null;
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : "Abmeldung fehlgeschlagen",
      });
    }
  }, []);

  const contextValue: AuthContextValue = {
    ...authState,
    login,
    logout,
    getValidAccessToken,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
